/**
 * Drawing the fields a firm added for itself, on the forms people actually use.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS IS NOT A `useZodForm`
 * ---------------------------------------------------------------------------
 *
 * Every other form in this app has a schema written next to it, which is right when
 * the shape is known at build time. These fields are not: they arrive from the server,
 * they differ per firm, and they change while the app is running. A schema rebuilt from
 * a query result on every render buys nothing and costs a re-render loop.
 *
 * So this keeps a plain `Record<string, unknown>` and hands it to the API as
 * `custom_fields`. The server validates it against the same definitions the CSV
 * importer uses — one implementation, so the form and a spreadsheet cannot disagree
 * about what a valid value is — and returns problems keyed by field, which
 * `problemsFrom` puts back under the right input.
 *
 * ---------------------------------------------------------------------------
 * THE VISIBILITY LABEL IS NOT DECORATION
 * ---------------------------------------------------------------------------
 *
 * A field marked client-visible is shown as such at the point of typing, not only in
 * settings. The person adding "possible sham marriage" to a record is rarely the
 * administrator who created the field, and by then the warning in settings is weeks
 * old and belongs to somebody else.
 */

import {
  STAFF,
  fieldErrors,
  qk,
  useApiQuery,
  type CustomFieldDefinition,
  type CustomFieldEntity,
  type CustomFieldValues,
} from '@legalcrm/api';
import {
  Badge,
  Checkbox,
  CheckboxGroup,
  Input,
  Select,
  Textarea,
} from '@legalcrm/ui';

const PATHS: Record<CustomFieldEntity, string> = {
  lead: STAFF.leadCustomFields,
  client: STAFF.clientCustomFields,
};

/**
 * This firm's fields for one kind of record.
 *
 * Returns a bare array — the endpoint is not paginated, because a firm with more than a
 * screenful of custom fields has a different problem than pagination solves.
 */
export function useCustomFieldDefinitions(entity: CustomFieldEntity) {
  const query = useApiQuery<CustomFieldDefinition[]>(
    qk.list(`${entity}-custom-fields`),
    PATHS[entity],
  );
  return {
    definitions: query.data ?? [],
    isLoading: query.isLoading,
  };
}

/** The prefix the server nests custom field errors under. */
const PREFIX = 'custom_fields.';

/**
 * Server-side validation errors for custom fields, keyed by field key.
 *
 * The API answers 400 with `{"custom_fields": {"visa_category": "..."}}`, which
 * `fieldErrors` flattens to `custom_fields.visa_category`. Stripping the prefix here
 * rather than reading the raw problem keeps one implementation of "what did the server
 * say about which field" — the first version of this read `error.data.custom_fields`,
 * which is not a shape this client ever produces, so every message was dropped and the
 * form appeared to reject a value without saying why.
 */
export function problemsFrom(error: unknown): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const [key, message] of Object.entries(fieldErrors(error))) {
    if (key.startsWith(PREFIX)) problems[key.slice(PREFIX.length)] = message;
  }
  return problems;
}

/**
 * The fields, as inputs.
 *
 * Renders nothing at all when the firm has added none — no heading, no empty box. A
 * section that is usually empty trains people to stop looking at it.
 */
export function CustomFieldInputs({
  entity,
  values,
  onChange,
  problems = {},
  heading = 'This firm’s own fields',
}: {
  entity: CustomFieldEntity;
  values: CustomFieldValues;
  onChange: (values: CustomFieldValues) => void;
  problems?: Record<string, string>;
  heading?: string;
}) {
  const { definitions } = useCustomFieldDefinitions(entity);
  if (definitions.length === 0) return null;

  const set = (key: string, value: CustomFieldValues[string]) =>
    onChange({ ...values, [key]: value });

  return (
    <fieldset className="space-y-4 border-t border-line pt-4">
      <legend className="sr-only">{heading}</legend>
      <p className="text-sm font-medium text-ink">{heading}</p>

      {definitions.map((definition) => (
        <CustomFieldInput
          key={definition.id}
          definition={definition}
          value={values[definition.key] ?? null}
          onChange={(value) => set(definition.key, value)}
          error={problems[definition.key]}
        />
      ))}
    </fieldset>
  );
}

/** One field. The label carries the visibility statement, so it cannot be missed. */
function CustomFieldInput({
  definition,
  value,
  onChange,
  error,
}: {
  definition: CustomFieldDefinition;
  value: CustomFieldValues[string];
  onChange: (value: CustomFieldValues[string]) => void;
  error?: string;
}) {
  const label = (
    <span className="inline-flex flex-wrap items-center gap-2">
      {definition.label}
      {/* Text, never colour alone. CR-25 is the leak class where somebody believed a
          panel was internal. */}
      {definition.is_client_visible && (
        <Badge tone="client-facing">The client can read this</Badge>
      )}
    </span>
  );

  const common = {
    label,
    hint: definition.help_text || undefined,
    error,
    required: definition.is_required,
  };

  const text = typeof value === 'string' ? value : value == null ? '' : String(value);

  switch (definition.field_type) {
    case 'long_text':
      return (
        <Textarea
          {...common}
          rows={3}
          value={text}
          onChange={(event) => onChange(event.currentTarget.value)}
        />
      );

    case 'boolean':
      return (
        <Checkbox
          {...common}
          checked={value === true}
          onChange={(event) => onChange(event.currentTarget.checked)}
        />
      );

    case 'select':
      return (
        <Select
          {...common}
          value={text}
          // A blank first option, always. Without one the browser pre-selects the
          // first real value and the field records a choice nobody made.
          placeholder="Not set"
          options={definition.options.map((option) => ({
            value: option,
            label: option,
          }))}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    case 'multi_select': {
      const selected = Array.isArray(value) ? value : [];
      return (
        // `legend`, not `label`: the group is a fieldset, and its accessible name has
        // to come from the legend or each option reads as unlabelled.
        <CheckboxGroup legend={label} hint={common.hint} error={error}>
          {definition.options.map((option) => (
            <Checkbox
              key={option}
              label={option}
              checked={selected.includes(option)}
              onChange={(event) =>
                onChange(
                  event.currentTarget.checked
                    ? [...selected, option]
                    : selected.filter((item) => item !== option),
                )
              }
            />
          ))}
        </CheckboxGroup>
      );
    }

    case 'date':
      return (
        <Input
          {...common}
          type="date"
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    case 'number':
      return (
        <Input
          {...common}
          type="number"
          step="any"
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    case 'email':
      return (
        <Input
          {...common}
          type="email"
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    case 'phone':
      return (
        <Input
          {...common}
          type="tel"
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    case 'url':
      return (
        <Input
          {...common}
          type="url"
          placeholder="https://"
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );

    default:
      return (
        <Input
          {...common}
          value={text}
          onChange={(event) => onChange(event.currentTarget.value || null)}
        />
      );
  }
}

/**
 * The same fields, read-only, for a record that already exists.
 *
 * Works from the definitions rather than from the stored keys, exactly as the server
 * does: a value whose definition has been removed is not shown, because if nothing
 * currently says a field exists then nobody has decided it should be read.
 */
export function CustomFieldsSummary({
  entity,
  values,
}: {
  entity: CustomFieldEntity;
  values: CustomFieldValues | undefined;
}) {
  const { definitions } = useCustomFieldDefinitions(entity);

  const present = definitions.filter((definition) => {
    const value = values?.[definition.key];
    return value !== undefined && value !== null && value !== '';
  });
  if (present.length === 0) return null;

  return (
    <dl className="grid gap-3 sm:grid-cols-2">
      {present.map((definition) => {
        const value = values![definition.key];
        return (
          <div key={definition.id}>
            <dt className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
              {definition.label}
              {definition.is_client_visible && (
                <Badge tone="client-facing">Client can read</Badge>
              )}
            </dt>
            <dd className="text-sm text-ink">
              {Array.isArray(value)
                ? value.join(', ')
                : typeof value === 'boolean'
                  ? value
                    ? 'Yes'
                    : 'No'
                  : String(value)}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
